import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { reloadCategories } from "../../lib/categories";
import { btnGhost, btnPrimary, inputCls } from "../../components/ui";

function CategoryRow({ c, onSaved }) {
  const [name, setName] = useState(c.name);
  const [sub, setSub] = useState("");
  const patch = async (body, ok) => {
    try { await api.patch(`/admin/categories/${c.key}`, body); toast.success(ok); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const subs = c.subcategories || [];
  return (
    <div className="flex flex-col gap-2 p-3 text-sm" data-testid={`admin-category-${c.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} w-56`} data-testid={`admin-category-name-${c.key}`} />
        {name.trim() !== c.name && <button onClick={() => patch({ name }, "Categoria renomeada")} className={`${btnPrimary} px-3 py-1.5`} data-testid={`admin-category-rename-${c.key}`}>Guardar</button>}
        <span className="text-xs text-gray-500">{c.products} produtos · ordem</span>
        <input type="number" min="0" defaultValue={c.order} onBlur={(e) => Number(e.target.value) !== c.order && patch({ order: Number(e.target.value) }, "Ordem atualizada")} className="w-16 rounded-lg border border-gray-200 px-2 py-1 text-sm" data-testid={`admin-category-order-${c.key}`} />
        <button onClick={() => patch({ active: !c.active }, c.active ? "Categoria ocultada" : "Categoria ativada")} className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${c.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-200"}`} data-testid={`admin-category-toggle-${c.key}`}>{c.active ? "Visível" : "Oculta"}</button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {subs.map((s) => <span key={s.key} className="flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs" data-testid={`admin-subcategory-${c.key}-${s.key}`}>{s.name}
          <button onClick={() => patch({ subcategories: subs.filter((x) => x.key !== s.key) }, "Subcategoria removida")} aria-label="Remover" data-testid={`admin-subcategory-remove-${c.key}-${s.key}`}><X className="h-3 w-3" /></button></span>)}
        <input placeholder="Nova subcategoria" value={sub} onChange={(e) => setSub(e.target.value)} className="w-40 rounded-lg border border-gray-200 px-2 py-1 text-xs" data-testid={`admin-subcategory-input-${c.key}`} />
        <button disabled={sub.trim().length < 2} onClick={() => { patch({ subcategories: [...subs, { name: sub.trim() }] }, "Subcategoria adicionada"); setSub(""); }} className={`${btnGhost} px-2 py-1`} data-testid={`admin-subcategory-add-${c.key}`}><Plus className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

function BrandRow({ b, onSaved }) {
  const [name, setName] = useState(b.name);
  const patch = async (body) => {
    try { await api.patch(`/admin/brands/${b.id}`, body); toast.success("Marca atualizada"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-2 p-2 text-sm" data-testid={`admin-brand-${b.id}`}>
      <input value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} flex-1`} data-testid={`admin-brand-name-${b.id}`} />
      {name.trim() !== b.name && <button onClick={() => patch({ name })} className={`${btnPrimary} px-3 py-1.5`} data-testid={`admin-brand-save-${b.id}`}>Guardar</button>}
      <span className="text-xs text-gray-500">{b.products}</span>
      <button onClick={() => patch({ active: !b.active })} className={`rounded-full px-3 py-1 text-xs font-semibold ${b.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-200"}`} data-testid={`admin-brand-toggle-${b.id}`}>{b.active ? "Ativa" : "Inativa"}</button>
    </div>
  );
}

export default function AdminCatalog() {
  const [t, setT] = useState(null);
  const [cat, setCat] = useState("");
  const [brand, setBrand] = useState("");
  const load = () => api.get("/admin/taxonomy").then(({ data }) => setT(data)).catch((e) => toast.error(errMsg(e)));
  const saved = () => { load(); reloadCategories(); };
  useEffect(() => { load(); }, []);
  const add = async (path, name, reset, ok) => {
    try { await api.post(path, { name }); toast.success(ok); reset(""); saved(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!t) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-[2fr,1fr]" data-testid="admin-catalog">
      <div className="bo-card divide-y divide-gray-50">
        <div className="flex gap-2 p-3"><h3 className="flex-1 font-bold">Categorias</h3>
          <input placeholder="Nova categoria" value={cat} onChange={(e) => setCat(e.target.value)} className={`${inputCls} w-48`} data-testid="admin-category-new-input" />
          <button disabled={cat.trim().length < 2} onClick={() => add("/admin/categories", cat, setCat, "Categoria criada")} className={btnPrimary} data-testid="admin-category-new-btn">Criar</button></div>
        {t.categories.map((c) => <CategoryRow key={`${c.key}-${c.name}-${(c.subcategories || []).length}-${c.active}`} c={c} onSaved={saved} />)}
        <p className="p-3 text-xs text-gray-500">Categorias com produtos não são apagadas: oculte-as para as retirar do site.</p>
      </div>
      <div className="bo-card divide-y divide-gray-50">
        <div className="flex gap-2 p-3"><h3 className="flex-1 font-bold">Marcas</h3>
          <input placeholder="Nova marca" value={brand} onChange={(e) => setBrand(e.target.value)} className={`${inputCls} w-36`} data-testid="admin-brand-new-input" />
          <button disabled={!brand.trim()} onClick={() => add("/admin/brands", brand, setBrand, "Marca criada")} className={btnPrimary} data-testid="admin-brand-new-btn">Criar</button></div>
        {t.brands.map((b) => <BrandRow key={`${b.id}-${b.name}-${b.active}`} b={b} onSaved={load} />)}
      </div>
    </div>
  );
}
